import React, { useState, useContext, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../css/TopNavbar.css";
import { formatCompanyName } from "../utils/format";
import { Menu, Headset, CircleUser, User, Lock, LogOut, ChevronDown, Search, Loader2, Check, Building2, BookOpen, Settings } from "lucide-react";
import { NewContext } from "../utils/ContextProvider";
import { removeUserSession } from "../utils/common";
import { getImagenPerfil, getEmpleadosSimpleDTO, postSwitchCompany } from "../utils/peticionesApi";
import NotificacionesPendientes from "../components/NotificacionesCobertura";
import ModalPendientes from "../components/ModalAlertasCobertura";
import ModalCambiarPassword from "../components/ModalCambiarPassword";
import ModalGenerico from "../components/ModalGenerico";
import AppVersion from "./AppVersion";
import {
  AVISO_ENTRA_POR_PORTAL, RUTA_LOGIN_PORTAL_LEGALES, esRolExterno, limpiarSesionesParaElPortal, payloadDe,
} from "../pages/admin/legales/legalesAlcance";

const ROLE_LABELS = {
  ROLE_ADMIN: "Administrador",
  ROLE_RRHH: "RRHH",
  ROLE_JEFE_AREA: "Jefe de Área",
  ROLE_SUPERVISOR: "Supervisor",
  ROLE_REFERENTE: "Supervisor",
  ROLE_OPERADOR: "Operador",
  ROLE_USER: "Empleado",
  ROLE_TECNICO: "Técnico",
};

const ROUTE_LABELS = {
  "/home": "Panel",
  "/RRHH": "RRHH",
  "/Presentismo": "Presentismo",
  "/SeguridadHigiene": "SGI",
  "/objetivos": "Empresas",
  "/sueldos": "Sueldos",
  "/facturacion": "Facturación",
  "/empleados": "Empleados",
  "/asistencias": "Asistencias",
  "/mis-recibos": "Mis Recibos",
  "/capacitaciones": "Capacitaciones",
  "/solicitudes": "Solicitudes",
  "/vacaciones": "Vacaciones",
  "/tickets": "Tickets",
  "/novedades": "Novedades",
  "/evaluaciones": "Evaluaciones",
  "/servicios-cubrir": "Servicios",
  "/tablero-control": "Tablero",
  "/EstadisticasPresentismo": "Estadísticas",
  "/control-horas-liquidacion": "Control de Horas",
  "/biblioteca-normas": "Biblioteca",
  "/suspensiones": "Sanciones",
  "/admin/rondas": "Rondas",
  "/admin/supervision": "Supervisión",
  "/admin/estructura": "Estructura",
  "/admin/empleados-inactivos": "Inactivos",
  "/miperfil": "Mi Perfil",
  "/calidad": "Calidad",
  "/reclamos": "Reclamos",
  "/habilitaciones": "Habilitaciones",
};

// Multi-level breadcrumbs: more specific routes → array of { label, path? }
// Matched longest-prefix-first; falls back to ROUTE_LABELS single-level
// If a segment has `path`, clicking it navigates there
const ROUTE_BREADCRUMBS = {
  "/novedades": [{ label: "Softnik" }, { label: "RRHH", path: "/RRHH" }, { label: "Novedades" }],
  "/admin/evaluaciones-desempeno/plantillas/nueva": [{ label: "Softnik" }, { label: "Evaluaciones", path: "/admin/evaluaciones-desempeno" }, { label: "Plantillas", path: "/admin/evaluaciones-desempeno/plantillas" }, { label: "Nueva" }],
  "/admin/evaluaciones-desempeno/plantillas":       [{ label: "Softnik" }, { label: "Evaluaciones", path: "/admin/evaluaciones-desempeno" }, { label: "Plantillas" }],
  "/admin/evaluaciones-desempeno/listado":    [{ label: "Softnik" }, { label: "Evaluaciones", path: "/admin/evaluaciones-desempeno" }, { label: "Listado" }],
  "/admin/evaluaciones-desempeno/nueva":      [{ label: "Softnik" }, { label: "Evaluaciones", path: "/admin/evaluaciones-desempeno" }, { label: "Nueva" }],
  "/admin/evaluaciones-desempeno/metricas":   [{ label: "Softnik" }, { label: "Evaluaciones", path: "/admin/evaluaciones-desempeno" }, { label: "Métricas" }],
  "/admin/evaluaciones-desempeno/":           [{ label: "Softnik" }, { label: "Evaluaciones", path: "/admin/evaluaciones-desempeno" }, { label: "Listado", path: "/admin/evaluaciones-desempeno/listado" }, { label: "Evaluación" }],
  "/admin/evaluaciones-desempeno":            [{ label: "Softnik" }, { label: "Evaluaciones" }],
  "/mis-evaluaciones":                        [{ label: "Softnik" }, { label: "Mis Evaluaciones" }],
  "/admin/armamentos/arma/":          [{ label: "Softnik" }, { label: "Armamentos", path: "/admin/armamentos" }, { label: "Ficha" }],
  "/admin/armamentos":                [{ label: "Softnik" }, { label: "Armamentos" }],
  "/admin/calidad/no-conformidades":  [{ label: "Softnik" }, { label: "SGI", path: "/SeguridadHigiene" }, { label: "Hallazgos" }],
  "/admin/calidad/indicadores":       [{ label: "Softnik" }, { label: "SGI", path: "/SeguridadHigiene" }, { label: "Indicadores" }],
  "/admin/calidad/encuestas":         [{ label: "Softnik" }, { label: "SGI", path: "/SeguridadHigiene" }, { label: "Encuestas" }],
  "/admin/calidad/autorizaciones":     [{ label: "Softnik" }, { label: "SGI", path: "/SeguridadHigiene" }, { label: "Autorizaciones" }],
  "/admin/reclamos":                  [{ label: "Softnik" }, { label: "SGI", path: "/SeguridadHigiene" }, { label: "Reclamos" }],
  "/admin/medicina-laboral/":         [{ label: "Softnik" }, { label: "Medicina Laboral", path: "/admin/medicina-laboral" }, { label: "Empleado" }],
  "/admin/medicina-laboral":          [{ label: "Softnik" }, { label: "Medicina Laboral" }],
  "/admin/flota/vehiculo/":           [{ label: "Softnik" }, { label: "Flota", path: "/admin/flota" }, { label: "Vehículo" }],
  "/admin/flota":                     [{ label: "Softnik" }, { label: "Flota" }],
  "/auditor/sesion/":                [{ label: "Auditor" }, { label: "Dashboard", path: "/auditor/dashboard" }, { label: "Auditoria" }],
  "/admin/auditor-checklist/sesion/":          [{ label: "Softnik" }, { label: "Auditorias", path: "/admin/auditor-checklist" }, { label: "Detalle Sesion" }],
  "/admin/auditor-checklist/checklist/nuevo": [{ label: "Softnik" }, { label: "Auditorias", path: "/admin/auditor-checklist" }, { label: "Nuevo Checklist" }],
  "/admin/auditor-checklist/checklist/":      [{ label: "Softnik" }, { label: "Auditorias", path: "/admin/auditor-checklist" }, { label: "Editar Checklist" }],
  "/admin/auditor-checklist/actas-visita/nueva": [{ label: "Softnik" }, { label: "Auditorias", path: "/admin/auditor-checklist" }, { label: "Nueva Acta" }],
  "/admin/auditor-checklist/actas-visita/":      [{ label: "Softnik" }, { label: "Auditorias", path: "/admin/auditor-checklist" }, { label: "Acta" }],
  "/admin/auditor-checklist":         [{ label: "Softnik" }, { label: "Auditorias" }],
  "/mis-auditorias/asignacion/":      [{ label: "Softnik" }, { label: "Mis Auditorias", path: "/mis-auditorias" }, { label: "Checklist" }],
  "/mis-auditorias":                  [{ label: "Softnik" }, { label: "Mis Auditorias" }],
  "/capacitaciones/estadisticasCap":  [{ label: "Softnik" }, { label: "SGI", path: "/SeguridadHigiene" }, { label: "Capacitaciones", path: "/capacitaciones" }, { label: "Estadísticas" }],
  "/capacitaciones/":                 [{ label: "Softnik" }, { label: "SGI", path: "/SeguridadHigiene" }, { label: "Capacitaciones", path: "/capacitaciones" }, { label: "Curso" }],
  "/capacitaciones":                  [{ label: "Softnik" }, { label: "SGI", path: "/SeguridadHigiene" }, { label: "Capacitaciones" }],
  "/mis-recibos":                      [{ label: "Softnik" }, { label: "Mis Recibos" }],
  "/sueldos/conceptos":                 [{ label: "Softnik" }, { label: "Sueldos", path: "/sueldos" }, { label: "Conceptos" }],
  "/sueldos/escalas-salariales":        [{ label: "Softnik" }, { label: "Sueldos", path: "/sueldos" }, { label: "Escalas Salariales" }],
  "/sueldos/feriados":                  [{ label: "Softnik" }, { label: "Sueldos", path: "/sueldos" }, { label: "Feriados" }],
  "/sueldos/tablas-referencia":         [{ label: "Softnik" }, { label: "Sueldos", path: "/sueldos" }, { label: "Tablas de Referencia" }],
  "/sueldos/empleador":                 [{ label: "Softnik" }, { label: "Sueldos", path: "/sueldos" }, { label: "Empleador" }],
  "/sueldos/novedades":                 [{ label: "Softnik" }, { label: "Sueldos", path: "/sueldos" }, { label: "Novedades" }],
  "/sueldos/liquidacion":               [{ label: "Softnik" }, { label: "Sueldos", path: "/sueldos" }, { label: "Liquidación" }],
  "/sueldos/base-aguinaldo":            [{ label: "Softnik" }, { label: "Sueldos", path: "/sueldos" }, { label: "Base del aguinaldo" }],
  "/sueldos/recibos":                   [{ label: "Softnik" }, { label: "Sueldos", path: "/sueldos" }, { label: "Recibos de sueldo" }],
  "/sueldos":                            [{ label: "Softnik" }, { label: "Sueldos" }],
  "/haberes":                            [{ label: "Softnik" }, { label: "Sueldos", path: "/sueldos" }, { label: "Recibos" }],
  "/solicitudes":                     [{ label: "Softnik" }, { label: "RRHH", path: "/RRHH" }, { label: "Solicitudes" }],
  "/asistencias":                    [{ label: "Softnik" }, { label: "Asistencias", path: "/Presentismo" }, { label: "Asistencias" }],
  "/servicios-cubrir":               [{ label: "Softnik" }, { label: "Asistencias", path: "/Presentismo" }, { label: "Servicios a Cubrir" }],
  "/licencias-panel":                [{ label: "Softnik" }, { label: "Asistencias", path: "/Presentismo" }, { label: "Panel de Licencias" }],
  "/EstadisticasPresentismo":        [{ label: "Softnik" }, { label: "Asistencias", path: "/Presentismo" }, { label: "Estadísticas" }],
  "/control-horas-liquidacion":      [{ label: "Softnik" }, { label: "Asistencias", path: "/Presentismo" }, { label: "Control de Horas" }],
  "/areas-departamentos":            [{ label: "Softnik" }, { label: "Asistencias", path: "/Presentismo" }, { label: "Reloj Biométrico" }],
  "/Dispositivos":                   [{ label: "Softnik" }, { label: "Configuraciones", path: "/admin/configuraciones" }, { label: "Dispositivos" }],
  "/admin/configuraciones":          [{ label: "Softnik" }, { label: "Configuraciones" }],
  "/admin/configuracion/whatsapp":   [{ label: "Softnik" }, { label: "Configuraciones", path: "/admin/configuraciones" }, { label: "WhatsApp" }],
  "/admin/configuracion/tableta":    [{ label: "Softnik" }, { label: "Configuraciones", path: "/admin/configuraciones" }, { label: "Tableta de Firma" }],
  "/tablero-control":                [{ label: "Softnik" }, { label: "Asistencias", path: "/asistencias" }, { label: "Tablero de Control" }],
  "/ausencias/nueva":                [{ label: "Softnik" }, { label: "Asistencias", path: "/Presentismo" }, { label: "Ausencias y Licencias" }],
  "/suspensiones/nueva":             [{ label: "Softnik" }, { label: "RRHH", path: "/RRHH" }, { label: "Sanciones", path: "/suspensiones" }, { label: "Nueva suspensión" }],
  "/suspensiones/editar":            [{ label: "Softnik" }, { label: "RRHH", path: "/RRHH" }, { label: "Sanciones", path: "/suspensiones" }, { label: "Suspensión" }],
  "/suspensiones":                   [{ label: "Softnik" }, { label: "RRHH", path: "/RRHH" }, { label: "Sanciones" }],
  "/admin/rondas/puntos-control":     [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Rondas", path: "/admin/rondas" }, { label: "Puntos de Control" }],
  "/admin/rondas/recorridos":         [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Rondas", path: "/admin/rondas" }, { label: "Recorridos" }],
  "/admin/rondas/configuracion":      [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Rondas", path: "/admin/rondas" }, { label: "Configuración" }],
  "/admin/rondas/realizar":           [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Rondas", path: "/admin/rondas" }, { label: "Realizar Ronda" }],
  "/admin/rondas/historial":          [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Rondas", path: "/admin/rondas" }, { label: "Historial" }],
  "/admin/rondas/reportes":           [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Rondas", path: "/admin/rondas" }, { label: "Reportes" }],
  "/admin/rondas/portal-clientes":    [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Rondas", path: "/admin/rondas" }, { label: "Portal Clientes" }],
  "/admin/rondas":                    [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Rondas" }],
  "/admin/accesos":                   [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Control de Accesos" }],
  "/admin/uniformes/solicitud":       [{ label: "Softnik" }, { label: "RRHH", path: "/RRHH" }, { label: "Uniformes", path: "/admin/uniformes" }, { label: "Solicitar Uniformes" }],
  "/admin/uniformes":                [{ label: "Softnik" }, { label: "RRHH", path: "/RRHH" }, { label: "Uniformes" }],
  "/admin/supervision/reportes":      [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Reportes y Métricas" }],
  "/admin/supervision/tablero-gerencial": [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Tablero Gerencial" }],
  "/admin/supervision/historial":     [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Historial de Visitas" }],
  "/admin/supervision/novedades":     [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Eventos de Seguridad" }],
  "/admin/supervision/minutas-reunion/": [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Minutas de Reunión", path: "/admin/supervision/minutas-reunion" }, { label: "Detalle" }],
  "/admin/supervision/minutas-reunion":  [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Minutas de Reunión" }],
  "/admin/supervision/simulacros/plantillas/nueva": [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Simulacros", path: "/admin/supervision/simulacros/menu" }, { label: "Plantillas", path: "/admin/supervision/simulacros/plantillas" }, { label: "Nueva" }],
  "/admin/supervision/simulacros/plantillas/":      [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Simulacros", path: "/admin/supervision/simulacros/menu" }, { label: "Plantillas", path: "/admin/supervision/simulacros/plantillas" }, { label: "Editar" }],
  "/admin/supervision/simulacros/plantillas":       [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Simulacros", path: "/admin/supervision/simulacros/menu" }, { label: "Plantillas" }],
  "/admin/supervision/simulacros/menu":             [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Simulacros" }],
  "/admin/supervision/simulacros/iniciar":          [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Simulacros", path: "/admin/supervision/simulacros/menu" }, { label: "Iniciar" }],
  "/admin/supervision/simulacros/stats":            [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Simulacros", path: "/admin/supervision/simulacros/menu" }, { label: "Reportes" }],
  "/admin/supervision/simulacros/":                 [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Simulacros", path: "/admin/supervision/simulacros/menu" }, { label: "Detalle" }],
  "/admin/supervision/simulacros":                  [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Simulacros", path: "/admin/supervision/simulacros/menu" }, { label: "Listado" }],
  "/admin/supervision/actas-visita/":  [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Actas de Visita", path: "/admin/supervision/actas-visita" }, { label: "Detalle" }],
  "/admin/supervision/actas-visita":  [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Actas de Visita" }],
  "/admin/supervision/informes-guardia/": [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Informes de Guardia", path: "/admin/supervision/informes-guardia" }, { label: "Detalle" }],
  "/admin/supervision/informes-guardia":  [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Informes de Guardia" }],
  "/admin/supervision/planificacion": [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Planificación" }],
  "/admin/supervision/directivas/": [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Directivas Particulares", path: "/admin/supervision/directivas" }, { label: "Detalle" }],
  "/admin/supervision/directivas":  [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Directivas Particulares" }],
  "/admin/supervision/libro-novedades/": [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Libro de Novedades Digital", path: "/admin/supervision/libro-novedades" }, { label: "Libros del objetivo" }],
  "/admin/supervision/libro-novedades":  [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Libro de Novedades Digital" }],
  "/admin/auditoria/sesion/":          [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Análisis de Riesgo", path: "/admin/auditoria/analisis-riesgo" }, { label: "Sesión" }],
  "/admin/auditoria/analisis-riesgo": [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Análisis de Riesgo" }],
  "/admin/auditoria/mis-auditorias":  [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Mis Análisis" }],
  "/ticket/":                         [{ label: "Softnik" }, { label: "Soporte", path: "/tickets" }, { label: "Tickets", path: "/tickets" }, { label: "Detalle" }],
  "/tickets":                        [{ label: "Softnik" }, { label: "Soporte" }, { label: "Tickets" }],
  "/empresa/":                       [{ label: "Softnik" }, { label: "Empresas", path: "/objetivos" }, { label: "Detalle" }],
  "/objetivos":                      [{ label: "Softnik" }, { label: "Empresas" }],
  // Técnica: los más específicos primero (se busca con startsWith en orden). «Stock» va sin path porque
  // ya no hay una pantalla «Stock»: cada solapa es un ítem del menú. «Técnica» tampoco lleva path:
  // /admin/tecnica sólo redirige a la primera sección, y si era la pantalla actual el clic dejaba una
  // entrada repetida en el historial (el primer Atrás no hacía nada). Para moverse está el menú lateral.
  // Editar una orden va en el bloque dinámico.
  "/admin/tecnica/clientes/bajas":                 [{ label: "Softnik" }, { label: "Técnica" }, { label: "Clientes", path: "/admin/tecnica/clientes" }, { label: "Bajas" }],
  "/admin/tecnica/clientes/":                      [{ label: "Softnik" }, { label: "Técnica" }, { label: "Clientes", path: "/admin/tecnica/clientes" }, { label: "Ficha" }],
  "/admin/tecnica/clientes":                       [{ label: "Softnik" }, { label: "Técnica" }, { label: "Clientes" }],
  "/admin/tecnica/solicitudes":                    [{ label: "Softnik" }, { label: "Técnica" }, { label: "Solicitudes" }],
  "/admin/tecnica/ordenes/recorridos/":            [{ label: "Softnik" }, { label: "Técnica" }, { label: "Órdenes", path: "/admin/tecnica/ordenes" }, { label: "Recorridos", path: "/admin/tecnica/ordenes/recorridos" }, { label: "Recorrido" }],
  "/admin/tecnica/ordenes/recorridos":             [{ label: "Softnik" }, { label: "Técnica" }, { label: "Órdenes", path: "/admin/tecnica/ordenes" }, { label: "Recorridos" }],
  "/admin/tecnica/ordenes/nueva":                  [{ label: "Softnik" }, { label: "Técnica" }, { label: "Órdenes", path: "/admin/tecnica/ordenes" }, { label: "Nueva orden" }],
  "/admin/tecnica/ordenes/":                       [{ label: "Softnik" }, { label: "Técnica" }, { label: "Órdenes", path: "/admin/tecnica/ordenes" }, { label: "Orden de trabajo" }],
  "/admin/tecnica/ordenes":                        [{ label: "Softnik" }, { label: "Técnica" }, { label: "Órdenes" }],
  "/admin/tecnica/estadisticas":                   [{ label: "Softnik" }, { label: "Técnica" }, { label: "Estadísticas" }],
  "/admin/tecnica/configuracion/autorizaciones":   [{ label: "Softnik" }, { label: "Técnica" }, { label: "Configuración", path: "/admin/tecnica/configuracion" }, { label: "Autorizaciones de órdenes" }],
  "/admin/tecnica/configuracion/permisos-stock":   [{ label: "Softnik" }, { label: "Técnica" }, { label: "Configuración", path: "/admin/tecnica/configuracion" }, { label: "Permisos de stock" }],
  "/admin/tecnica/configuracion":                  [{ label: "Softnik" }, { label: "Técnica" }, { label: "Configuración" }, { label: "Notificaciones" }],
  "/admin/tecnica/stock/tecnicos/":                [{ label: "Softnik" }, { label: "Técnica" }, { label: "Stock" }, { label: "Técnicos", path: "/admin/tecnica/stock/tecnicos" }, { label: "Ficha" }],
  "/admin/tecnica/stock/articulo/":                [{ label: "Softnik" }, { label: "Técnica" }, { label: "Stock" }, { label: "Catálogo", path: "/admin/tecnica/stock/catalogo" }, { label: "Artículo" }],
  "/admin/tecnica/stock/saldos":                   [{ label: "Softnik" }, { label: "Técnica" }, { label: "Stock" }, { label: "Saldos" }],
  "/admin/tecnica/stock/remitos":                  [{ label: "Softnik" }, { label: "Técnica" }, { label: "Stock" }, { label: "Remitos" }],
  "/admin/tecnica/stock/movimientos":              [{ label: "Softnik" }, { label: "Técnica" }, { label: "Stock" }, { label: "Movimientos" }],
  "/admin/tecnica/stock/catalogo":                 [{ label: "Softnik" }, { label: "Técnica" }, { label: "Stock" }, { label: "Catálogo" }],
  "/admin/tecnica/stock/herramientas":             [{ label: "Softnik" }, { label: "Técnica" }, { label: "Stock" }, { label: "Herramientas" }],
  "/admin/tecnica/stock/kits":                     [{ label: "Softnik" }, { label: "Técnica" }, { label: "Stock" }, { label: "Kits" }],
  "/admin/tecnica/stock/tecnicos":                 [{ label: "Softnik" }, { label: "Técnica" }, { label: "Stock" }, { label: "Técnicos" }],
  "/admin/tecnica/incidentes/":                    [{ label: "Softnik" }, { label: "Técnica" }, { label: "Incidentes", path: "/admin/tecnica/incidentes" }, { label: "Ficha" }],
  "/admin/tecnica/incidentes":                     [{ label: "Softnik" }, { label: "Técnica" }, { label: "Incidentes" }],
  "/admin/tecnica/diferencias":                    [{ label: "Softnik" }, { label: "Técnica" }, { label: "Diferencias" }],
  "/admin/tecnica/mi-stock":                       [{ label: "Softnik" }, { label: "Técnica" }, { label: "Mi stock" }],
  "/admin/tecnica":                                [{ label: "Softnik" }, { label: "Técnica" }],
  "/admin/habilitaciones-personal/":  [{ label: "Softnik" }, { label: "RRHH", path: "/RRHH" }, { label: "Habilitaciones", path: "/admin/habilitaciones-personal" }, { label: "Empleado" }],
  "/admin/habilitaciones-personal":   [{ label: "Softnik" }, { label: "RRHH", path: "/RRHH" }, { label: "Habilitaciones" }],
  "/empleados":                      [{ label: "Softnik" }, { label: "RRHH", path: "/RRHH" }, { label: "Empleados" }],
  "/admin/nuevos-ingresos":          [{ label: "Softnik" }, { label: "RRHH", path: "/RRHH" }, { label: "Empleados", path: "/empleados" }, { label: "Nuevos Ingresos" }],
  "/admin/empleados-inactivos":      [{ label: "Softnik" }, { label: "RRHH", path: "/RRHH" }, { label: "Empleados", path: "/empleados" }, { label: "Desvinculados" }],
  "/admin/Pendientes":               [{ label: "Softnik" }, { label: "RRHH", path: "/RRHH" }, { label: "Empleados", path: "/empleados" }, { label: "Pendientes" }],
};

function TopNavbar() {
  const {
    getCompany,
    getRol,
    getDisplayRol,
    getEmail,
    setFlag,
    flag,
    profileImage,
    setProfileImage,
    getId,
    setStates,
    selectedEmployee,
    controlToken,
  } = useContext(NewContext);


  const navigate = useNavigate();
  const location = useLocation();
  const [imageUrl, setImageUrl] = useState(null);
  const [showPendientes, setShowPendientes] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [switchingCompany, setSwitchingCompany] = useState(null);
  const dropdownRef = useRef(null);
  const companyDropdownRef = useRef(null);

  // ── Employee search ──
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchHighlight, setSearchHighlight] = useState(-1);
  const searchInputRef = useRef(null);
  const searchWrapperRef = useRef(null);
  const searchAbortRef = useRef(null);
  const searchDebounceRef = useRef(null);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(e.target)) {
        setShowCompanyDropdown(false);
      }
    };
    if (showDropdown || showCompanyDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDropdown, showCompanyDropdown]);

  useEffect(() => {
    const isMobile = window.innerWidth <= 1080;
    if (getId && !profileImage && !isMobile) {
      cargarImagenPerfil();
    }
  }, [getId, profileImage]);

  const [showSupportModal, setShowSupportModal] = useState(false);

  const openWhatsApp = () => {
    const phoneNumber = "+5491121660744";
    const message = "Hola, estoy utilizando la aplicación para control de presentismo y necesito soporte. ¿Podrían ayudarme, por favor?";
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  const handleSupportClick = () => {
    const isAdminRrhh = getRol === "ROLE_ADMIN" || getRol === "ROLE_RRHH";
    if (isAdminRrhh) {
      setShowSupportModal(true);
    } else {
      openWhatsApp();
    }
  };

  const cargarImagenPerfil = async () => {
    try {
      const imgRes = await getImagenPerfil(getId);
      const objectUrl = URL.createObjectURL(imgRes.data);
      setImageUrl(objectUrl);
      setProfileImage(objectUrl);
    } catch (error) {
      console.warn("📷 Imagen de perfil no encontrada");
      setImageUrl(null);
    }
  };

  const handleLogout = () => {
    setStates();
    removeUserSession();
    navigate("/");
  };

  // ── Company switch ──
  const availableCompanies = (() => {
    try {
      const raw = sessionStorage.getItem("companies");
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch { return []; }
  })();
  const canSwitchCompany = availableCompanies.length > 1;

  const handleSwitchCompany = async (targetCompany) => {
    if (targetCompany === getCompany || switchingCompany) return;
    setSwitchingCompany(targetCompany);
    try {
      const res = await postSwitchCompany(targetCompany);
      const newToken = res.data;
      setShowCompanyDropdown(false);
      // Un token con el rol de abogado externo no sirve en la app (fuera de /api/legales recibe 403): no se guarda y
      // va al login del portal, como en Login.jsx.
      if (esRolExterno(payloadDe(newToken))) {
        limpiarSesionesParaElPortal();
        navigate(RUTA_LOGIN_PORTAL_LEGALES, { state: { aviso: AVISO_ENTRA_POR_PORTAL } });
        return;
      }
      sessionStorage.setItem("access_token", newToken);
      sessionStorage.setItem("company_selected", targetCompany);
      await controlToken?.(newToken);
      navigate("/home/");
      window.location.reload();
    } catch (e) {
      console.error("Error al cambiar empresa:", e);
      alert(e?.response?.data?.message || "Error al cambiar de empresa");
    } finally {
      setSwitchingCompany(null);
    }
  };

  // ── Search: debounced API call ──
  const doSearch = useCallback((q) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (searchAbortRef.current) searchAbortRef.current.abort();

    if (!q || q.trim().length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    searchDebounceRef.current = setTimeout(async () => {
      try {
        searchAbortRef.current = new AbortController();
        const res = await getEmpleadosSimpleDTO(0, 8, {
          q: q.trim(),
          signal: searchAbortRef.current.signal,
        });
        const content = res?.data?.content ?? [];
        setSearchResults(content.map((e) => ({
          id: String(e.uuid),
          name: `${e.firstName ?? ""} ${e.lastName ?? ""}`.trim(),
          nationalId: e.nationalId ?? "",
          employeeNumber: e.employeeNumber ?? "",
        })));
        setSearchHighlight(-1);
      } catch (err) {
        if (err?.name !== "AbortError") setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  }, []);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    setSearchOpen(true);
    doSearch(val);
  };

  const handleSearchSelect = (emp) => {
    setSearchOpen(false);
    setSearchQuery("");
    setSearchResults([]);
    navigate(`/empleado/${emp.id}`);
  };

  const handleSearchKeyDown = (e) => {
    if (!searchOpen || searchResults.length === 0) {
      if (e.key === "Escape") {
        setSearchOpen(false);
        searchInputRef.current?.blur();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSearchHighlight((prev) => (prev < searchResults.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSearchHighlight((prev) => (prev > 0 ? prev - 1 : searchResults.length - 1));
    } else if (e.key === "Enter" && searchHighlight >= 0) {
      e.preventDefault();
      handleSearchSelect(searchResults[searchHighlight]);
    } else if (e.key === "Escape") {
      setSearchOpen(false);
      searchInputRef.current?.blur();
    }
  };

  // ⌘K / Ctrl+K shortcut
  useEffect(() => {
    const handleGlobalKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
        setSearchOpen(true);
      }
    };
    document.addEventListener("keydown", handleGlobalKey);
    return () => document.removeEventListener("keydown", handleGlobalKey);
  }, []);

  // Close search dropdown on click outside
  useEffect(() => {
    const handleClickOutsideSearch = (e) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    if (searchOpen) {
      document.addEventListener("mousedown", handleClickOutsideSearch);
    }
    return () => document.removeEventListener("mousedown", handleClickOutsideSearch);
  }, [searchOpen]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      if (searchAbortRef.current) searchAbortRef.current.abort();
    };
  }, []);

  const userName = selectedEmployee
    ? `${selectedEmployee.person?.name || ""} ${selectedEmployee.person?.lastname || ""}`.trim()
    : "";
  const userRoleLabel = ROLE_LABELS[getDisplayRol] || ROLE_LABELS[getRol] || "";

  // Company pill initials
  const companyName = formatCompanyName(getCompany);
  const companyInitials = companyName.substring(0, 2).toUpperCase();

  // Breadcrumb from current route (multi-level first, then single-level fallback)
  const currentPath = location.pathname;
  const isAdminOrRrhh = getRol === "ROLE_ADMIN" || getRol === "ROLE_RRHH";
  const rawBreadcrumbSegments = (() => {
    // Dynamic nested routes (ID in the middle) — must check before generic startsWith
    if (/^\/empresa\/\d+\/informe-gestion/.test(currentPath)) {
      return [{ label: "Softnik" }, { label: "Empresas", path: "/objetivos" }, { label: "Informe de Gestión" }];
    }
    // Editar una OT lleva el id en el medio: la miga «Orden» vuelve a esa orden, no a la lista.
    const editarOt = currentPath.match(/^\/admin\/tecnica\/ordenes\/([^/]+)\/editar/);
    if (editarOt) {
      return [{ label: "Softnik" }, { label: "Técnica" }, { label: "Órdenes", path: "/admin/tecnica/ordenes" }, { label: "Orden", path: `/admin/tecnica/ordenes/${editarOt[1]}` }, { label: "Editar" }];
    }
    // Las fojas de un libro llevan el id del objetivo en el medio: la miga «Libros del objetivo» vuelve a ESE objetivo.
    const librosDeObjetivo = currentPath.match(/^\/admin\/supervision\/libro-novedades\/([^/]+)\/libros\/[^/]+/);
    if (librosDeObjetivo) {
      return [{ label: "Softnik" }, { label: "Supervisión", path: "/admin/supervision" }, { label: "Libro de Novedades Digital", path: "/admin/supervision/libro-novedades" }, { label: "Libros del objetivo", path: `/admin/supervision/libro-novedades/${librosDeObjetivo[1]}` }, { label: "Fojas" }];
    }
    return Object.entries(ROUTE_BREADCRUMBS).find(([path]) =>
      currentPath.startsWith(path)
    )?.[1] || null;
  })();
  const breadcrumbSegments = rawBreadcrumbSegments && !isAdminOrRrhh
    ? rawBreadcrumbSegments.map(seg =>
        seg.path === "/admin/supervision/simulacros/menu"
          ? { ...seg, path: "/admin/supervision/simulacros" }
          : seg
      )
    : rawBreadcrumbSegments;
  const breadcrumbLabel = !breadcrumbSegments
    ? (Object.entries(ROUTE_LABELS).find(([path]) => currentPath.startsWith(path))?.[1] || "")
    : "";

  // Avatar initials
  const avatarInitials = userName
    ? userName.split(" ").map(w => w[0]).join("").substring(0, 2).toUpperCase()
    : (getEmail ? getEmail[0].toUpperCase() : "U");

  return (
    <>
      <header className="app-topbar">
        {/* Mobile: logo + hamburger */}
        <div className="topbar-mobile-left">
          <img
            src={require("../image/Logo-softnik-positivo-color.png")}
            alt="Logo-softnik"
            className="topbar-mobile-logo"
          />
        </div>

        {/* Company pill */}
        <div className="company-pill-wrapper" ref={companyDropdownRef}>
          <div
            className={`company-pill${canSwitchCompany ? "" : " company-pill--single"}`}
            onClick={() => canSwitchCompany && setShowCompanyDropdown((p) => !p)}
          >
            <span className="swatch">{companyInitials}</span>
            <span>{companyName}</span>
            {canSwitchCompany && <ChevronDown size={14} className="company-pill-arrow" />}
          </div>

          {showCompanyDropdown && canSwitchCompany && (
            <div className="company-dropdown">
              <div className="company-dropdown-title">Cambiar empresa</div>
              {availableCompanies.map((c) => {
                const isCurrent = c === getCompany;
                const isSwitching = switchingCompany === c;
                return (
                  <button
                    key={c}
                    className={`company-dropdown-item${isCurrent ? " company-dropdown-item--active" : ""}`}
                    onClick={() => handleSwitchCompany(c)}
                    disabled={isCurrent || !!switchingCompany}
                  >
                    <Building2 size={14} className="company-dropdown-item-icon" />
                    <span className="company-dropdown-item-name">{formatCompanyName(c)}</span>
                    {isCurrent && <Check size={14} className="company-dropdown-item-check" />}
                    {isSwitching && <Loader2 size={14} className="lucide-spin company-dropdown-item-check" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Breadcrumbs */}
        {(breadcrumbSegments || breadcrumbLabel) && (
          <div className="topbar-crumbs mono">
            {breadcrumbSegments ? (
              breadcrumbSegments.map((seg, i) => {
                const isLast = i === breadcrumbSegments.length - 1;
                const isClickable = !isLast && seg.path;
                return (
                  <React.Fragment key={i}>
                    {i > 0 && <span className="sep">/</span>}
                    {isClickable ? (
                      <span className="crumb-link" onClick={() => navigate(seg.path)}>{seg.label}</span>
                    ) : (
                      <span className={isLast ? "current" : ""}>{seg.label}</span>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <>
                <span>Softnik</span>
                <span className="sep">/</span>
                <span className="current">{breadcrumbLabel}</span>
              </>
            )}
          </div>
        )}

        {/* Search — oculto para ROLE_USER y ROLE_TECNICO */}
        {(getRol === "ROLE_USER" || getRol === "ROLE_TECNICO") && <div style={{ flex: 1 }} />}
        {getRol !== "ROLE_USER" && getRol !== "ROLE_TECNICO" && (
        <div className="topbar-search" ref={searchWrapperRef}>
          <div className={`input${searchOpen && searchQuery.length >= 2 ? " topbar-search--active" : ""}`}>
            <span className="ico">
              {searchLoading ? <Loader2 size={14} className="lucide-spin" /> : <Search size={14} />}
            </span>
            <input
              ref={searchInputRef}
              placeholder="Buscar empleados…"
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={() => { if (searchQuery.length >= 2) setSearchOpen(true); }}
              onKeyDown={handleSearchKeyDown}
              autoComplete="off"
            />
            <span className="topbar-search-hint mono">⌘K</span>
          </div>

          {searchOpen && searchQuery.length >= 2 && (
            <div className="topbar-search-dropdown">
              {searchLoading && searchResults.length === 0 ? (
                <div className="topbar-search-empty">Buscando…</div>
              ) : searchResults.length === 0 ? (
                <div className="topbar-search-empty">Sin resultados</div>
              ) : (
                searchResults.map((emp, idx) => (
                  <div
                    key={emp.id}
                    className={`topbar-search-item${idx === searchHighlight ? " topbar-search-item--active" : ""}`}
                    onMouseEnter={() => setSearchHighlight(idx)}
                    onMouseDown={(e) => { e.preventDefault(); handleSearchSelect(emp); }}
                  >
                    <div className="topbar-search-item__avatar">
                      {emp.name.split(" ").map(w => w[0]).join("").substring(0, 2).toUpperCase()}
                    </div>
                    <div className="topbar-search-item__info">
                      <span className="topbar-search-item__name">{emp.name}</span>
                      <span className="topbar-search-item__meta">
                        {emp.employeeNumber ? `Leg. ${emp.employeeNumber}` : ""}
                        {emp.employeeNumber && emp.nationalId ? " · " : ""}
                        {emp.nationalId ? `DNI ${emp.nationalId}` : ""}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
        )}

        {/* Right actions */}
        <div className="topbar-actions">
          {/* Support */}
          <button className="topbar-icon-btn" onClick={handleSupportClick} title="Soporte">
            <Headset size={16} />
          </button>

          {/* Notifications */}
          <NotificacionesPendientes
            onOpenDashboard={() => setShowPendientes(true)}
          />

          {/* Hamburger (mobile) */}
          <button className="topbar-hamburger" onClick={() => setFlag(!flag)}>
            <Menu size={16} />
          </button>

          {/* Avatar */}
          <div className="avatar-wrapper" ref={dropdownRef}>
            <div
              className="topbar-avatar"
              onClick={() => setShowDropdown((prev) => !prev)}
            >
              {profileImage ? (
                <img src={profileImage} alt="perfil" className="topbar-avatar-img" />
              ) : (
                <span>{avatarInitials}</span>
              )}
            </div>

            {showDropdown && (
              <div className="avatar-dropdown">
                <div className="avatar-dropdown-header">
                  <div className="avatar-dropdown-photo">
                    {profileImage ? (
                      <img src={profileImage} alt="perfil" />
                    ) : (
                      <CircleUser size={22} />
                    )}
                  </div>
                  <div className="avatar-dropdown-info">
                    {userName && <span className="avatar-dropdown-name">{userName}</span>}
                    {getEmail && <span className="avatar-dropdown-email">{getEmail}</span>}
                    {userRoleLabel && <span className="avatar-dropdown-role">{userRoleLabel}</span>}
                  </div>
                </div>

                <div className="avatar-dropdown-divider" />

                <button
                  className="avatar-dropdown-item"
                  onClick={() => { setShowDropdown(false); navigate("/miperfil"); }}
                >
                  <User size={14} />
                  <span>Mi Perfil</span>
                </button>

                <button
                  className="avatar-dropdown-item"
                  onClick={() => { setShowDropdown(false); setShowChangePassword(true); }}
                >
                  <Lock size={14} />
                  <span>Cambiar Contraseña</span>
                </button>

                {getRol !== "ROLE_USER" && getRol !== "ROLE_TECNICO" && (
                  <button
                    className="avatar-dropdown-item"
                    onClick={() => { setShowDropdown(false); window.open("https://drive.google.com/drive/folders/14taOZZPHeEev-PdbtNEkYMnGdgKCxoqo?usp=sharing", "_blank"); }}
                  >
                    <BookOpen size={14} />
                    <span>Manual de Usuario</span>
                  </button>
                )}

                {getRol !== "ROLE_USER" && getRol !== "ROLE_TECNICO" && (
                  <button
                    className="avatar-dropdown-item"
                    onClick={() => { setShowDropdown(false); navigate("/admin/configuraciones"); }}
                  >
                    <Settings size={14} />
                    <span>Configuraciones</span>
                  </button>
                )}

                <div className="avatar-dropdown-divider" />

                <button
                  className="avatar-dropdown-item avatar-dropdown-logout"
                  onClick={() => { setShowDropdown(false); handleLogout(); }}
                >
                  <LogOut size={14} />
                  <span>Cerrar Sesión</span>
                </button>

                <AppVersion className="app-version--menu" />
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Modal de Pendientes */}
      <ModalPendientes
        show={showPendientes}
        onClose={() => setShowPendientes(false)}
      />

      {/* Modal Cambiar Contraseña */}
      <ModalCambiarPassword
        show={showChangePassword}
        onClose={() => setShowChangePassword(false)}
      />

      {/* Modal Soporte (ADMIN/RRHH) */}
      <ModalGenerico
        show={showSupportModal}
        onClose={() => setShowSupportModal(false)}
        titulo="Soporte"
        size="sm"
      >
        <p style={{ fontSize: "0.9rem", color: "var(--text-secondary)", marginBottom: "1.25rem" }}>
          ¿Cómo querés contactarnos?
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          <button
            onClick={() => { setShowSupportModal(false); navigate("/admin/soporte"); }}
            style={{
              display: "flex", alignItems: "center", gap: "0.85rem",
              padding: "1rem 1.25rem", background: "var(--bg-soft, #f7f8fb)",
              border: "1px solid var(--border-subtle, #e6e8ee)", borderRadius: "var(--radius-lg, 10px)",
              cursor: "pointer", textAlign: "left", fontFamily: "inherit", transition: "all 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--accent, #005EAE)"; e.currentTarget.style.background = "var(--color-primary-50, #ECEFF7)"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border-subtle, #e6e8ee)"; e.currentTarget.style.background = "var(--bg-soft, #f7f8fb)"; }}
          >
            <div style={{
              width: 42, height: 42, borderRadius: "var(--radius-md, 8px)",
              background: "var(--accent, #005EAE)", color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <Headset size={18} />
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: "0.9rem", color: "var(--text-primary, #0f1420)" }}>Ticket de soporte</div>
              <div style={{ fontSize: "0.8rem", color: "var(--text-muted, #9ba1b0)", marginTop: 2 }}>Creá un ticket y hacé seguimiento</div>
            </div>
          </button>

          <button
            onClick={() => { setShowSupportModal(false); openWhatsApp(); }}
            style={{
              display: "flex", alignItems: "center", gap: "0.85rem",
              padding: "1rem 1.25rem", background: "var(--bg-soft, #f7f8fb)",
              border: "1px solid var(--border-subtle, #e6e8ee)", borderRadius: "var(--radius-lg, 10px)",
              cursor: "pointer", textAlign: "left", fontFamily: "inherit", transition: "all 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "#25D366"; e.currentTarget.style.background = "#f0fdf4"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border-subtle, #e6e8ee)"; e.currentTarget.style.background = "var(--bg-soft, #f7f8fb)"; }}
          >
            <div style={{
              width: 42, height: 42, borderRadius: "var(--radius-md, 8px)",
              background: "#25D366", color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              fontSize: "1.2rem",
            }}>
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: "0.9rem", color: "var(--text-primary, #0f1420)" }}>Consulta por WhatsApp</div>
              <div style={{ fontSize: "0.8rem", color: "var(--text-muted, #9ba1b0)", marginTop: 2 }}>Respuesta rápida por chat</div>
            </div>
          </button>
        </div>
      </ModalGenerico>

    </>
  );
}

export default TopNavbar;
